import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  TextField,
  Typography,
  List,
  ListItem,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { useQuery } from 'react-query';
import client from "../../../../../API";
import { toast } from 'sonner';

const EmailCRMDialog = ({ open, onClose, userData }) => {
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailPreview, setEmailPreview] = useState(''); // Added for HTML preview
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const { data: campaigns, isLoading: isLoadingCampaigns } = useQuery('campaigns', client.campaigns.list);
  const { data: creators, isLoading: isLoadingCreators } = useQuery('creators', client.creators.list);

  // Convert email body text to HTML preview
  const generatePreview = () => {
    const formattedBody = emailBody
      .replace(/\n/g, '<br>') // Replace newlines with line breaks
      .replace(/{first_name}/g, 'User'); // Replace placeholders for preview
    setEmailPreview(formattedBody);
  };

  const handleDragStart = (e, text) => {
    e.dataTransfer.setData('text/plain', text);
  };

  const handleDrop = (e, setState) => {
    const text = e.dataTransfer.getData('text/plain');
    const cursorPosition = e.target.selectionStart;
    const currentValue = e.target.value;
    const newValue = currentValue.slice(0, cursorPosition) + text + currentValue.slice(cursorPosition);
    setState(newValue);
    e.preventDefault();
  };

  const handleInsertLink = () => {
    if (selectedCreator && discountPercentage) {
      const link = `https://www.useblitz.co/creators/${selectedCreator.creator}/promotional/${discountPercentage}`;
      setEmailBody((prevBody) => `${prevBody}\n[Creator Link](${link})`);
    }
  };

  const handleSendUpdate = async () => {
    setIsSending(true);

    // Prepare payload
    const updates = userData.map((user) => ({
      email: user.email,
      subject: emailSubject.replace(/{first_name}/g, user.first_name || 'User'),
      body: emailBody.replace(/{first_name}/g, user.first_name || 'User')
    }));

    if (process.env.NODE_ENV === 'development') {
      console.log('Sending updates:', updates);
    }

    try {
      const result = await client.crm.update(updates);

      if (process.env.NODE_ENV === 'development') {
        console.log('Updates sent:', result);
      }

      toast.success('Email updates sent successfully', {
        description: `${result?.length || 0} recipients notified`
      });

      // Optionally update CRM contacts
      await client.crm.update_contacts({ updates, note: `Last email - ${emailSubject}` });

      onClose();
    } catch (error) {
      toast.error('Failed to send updates', {
        description: error.message
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: '80vh' } }} // Custom width and height
    >
      <DialogTitle>Send CRM Updates</DialogTitle>
      <DialogContent>
        <FormGroup>
          <Typography variant="subtitle1" gutterBottom>
            Selected Users to Receive Updates:
          </Typography>
          <List dense>
            {userData.map((user) => (
              <ListItem key={user.id}>
                {user.email}
              </ListItem>
            ))}
          </List>
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, '{first_name}')}
            style={{
              padding: '5px',
              border: '1px dashed grey',
              display: 'inline-block',
              marginBottom: '10px',
              cursor: 'grab'
            }}
          >
            Drag to insert {`{first_name}`}
          </div>
          <Autocomplete
            options={campaigns || []}
            getOptionLabel={(option) => option.name}
            loading={isLoadingCampaigns}
            onChange={(event, newValue) => setSelectedCampaign(newValue)}
            renderInput={(params) => <TextField {...params} label="Select Campaign" variant="outlined" margin="normal" />}
          />
          <Autocomplete
            options={creators || []}
            getOptionLabel={(option) => option.creator}
            loading={isLoadingCreators}
            onChange={(event, newValue) => setSelectedCreator(newValue)}
            renderInput={(params) => <TextField {...params} label="Select Creator" variant="outlined" margin="normal" />}
          />
          <TextField
            label="Discount Percentage"
            variant="outlined"
            margin="normal"
            value={discountPercentage}
            onChange={(e) => setDiscountPercentage(e.target.value)}
          />
          <Button
            onClick={handleInsertLink}
            disabled={!selectedCreator || !discountPercentage}
            color="primary"
            variant="contained"
            style={{ marginBottom: '10px' }}
          >
            Insert Promotional Link
          </Button>
          <TextField
            label="Email Subject"
            variant="outlined"
            fullWidth
            margin="normal"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            onDrop={(e) => handleDrop(e, setEmailSubject)}
            onDragOver={(e) => e.preventDefault()}
          />
          <TextField
            label="Email Body"
            variant="outlined"
            multiline
            rows={8}
            fullWidth
            margin="normal"
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            onBlur={generatePreview} // Generate HTML preview on blur
            onDrop={(e) => handleDrop(e, setEmailBody)}
            onDragOver={(e) => e.preventDefault()}
          />
          <Typography variant="subtitle1" gutterBottom>
            Email Preview (HTML):
          </Typography>
          <div
            dangerouslySetInnerHTML={{ __html: emailPreview }}
            style={{
              padding: '10px',
              border: '1px solid grey',
              borderRadius: '5px',
              backgroundColor: '#f9f9f9',
              maxHeight: '200px',
              overflow: 'auto'
            }}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button onClick={handleSendUpdate} color="primary" variant="contained" disabled={isSending}>
          {isSending ? <CircularProgress size={24} /> : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailCRMDialog;
